import {
  authEndpointOf,
  err,
  ok,
  parseSubscriptionFeedPage,
  parseSubscriptionList,
  resolveInstanceUrl,
  scopesAllow,
  subscriptionChangePath,
  subscriptionFeedWireParams,
  SUBSCRIPTION_FEED_PATH,
  SUBSCRIPTIONS_PATH,
  TOKEN_REGISTER_PATH,
  TOKEN_UNREGISTER_PATH,
  withQuery,
  type AbortLike,
  type AuthScope,
  type HttpMethod,
  type ParsedList,
  type Result,
  type SubscriptionFeedPage,
  type SubscriptionRef,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { readJsonBody } from './json';
import type { AuthApiProblem } from './problem';

/**
 * The authenticated endpoints. The bearer header is *not* attached here — that happens inside the
 * transport (R9, origin-asserted), so nothing at this layer can forget it or leak it.
 */

/**
 * `ScopeProblem` and `AuthApiProblem` are declared in `api/problem.ts` since 4a, with the other problem
 * shapes and the words for them, and re-exported here because that is where every caller looks for
 * them. See `api/videos.ts` for why the declarations moved and the import paths did not.
 */
export type { AuthApiProblem, ScopeProblem } from './problem';

/**
 * **Every authenticated query key starts with this, and that is a convention with teeth.**
 *
 * Signing out has to drop the data the token bought — a subscription feed or a history list left in
 * the query cache would keep rendering after the credential is gone, which reads as "sign-out did not
 * work" and is a small privacy leak on a shared machine. `queryClient.clear()` would do it, at the
 * price of also throwing away Home, Trending and every channel page; removing this prefix throws away
 * exactly what belongs to the session. 3b–3d must key their queries under it.
 */
export const AUTH_QUERY_KEY = 'auth';

export async function authGet(
  platform: Platform,
  path: string,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<unknown, AuthApiProblem>> {
  const url = resolveInstanceUrl(platform.config, path);
  if (url === null) return err({ kind: 'bad-url', path });
  return await authGetUrl(platform, path, url, grantedScopes, signal);
}

/**
 * The same call for an endpoint that carries query parameters. Split out because the scope check must
 * read the **path** — `authEndpointOf` cuts at the first `?`, so passing a built URL works, but the
 * `bad-url` arm wants the path either way and building it twice is how the two drift apart.
 */
export async function authGetUrl(
  platform: Platform,
  path: string,
  url: string,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<unknown, AuthApiProblem>> {
  const endpoint = authEndpointOf(path);
  if (endpoint !== null && grantedScopes !== null && !scopesAllow(grantedScopes, 'GET', endpoint)) {
    return err({ kind: 'out-of-scope', method: 'GET', endpoint });
  }

  const response = await platform.transport.send({ method: 'GET', url, headers: {}, signal });
  if (!response.ok) return err({ kind: 'transport', error: response.error });

  // `readJsonBody` rather than the transport helper beside it: the scope check above had to run first,
  // and it needs the path. The zero-byte `200` reading is 3c's, lifted into `api/json.ts` in 4a.
  return readJsonBody(response.value.body);
}

/**
 * A write. `GET`'s three siblings, and the differences are not incidental:
 *
 * **The scope check is enforced here rather than left to the instance, and this is the one place where
 * pre-refusing is right.** `authGetUrl` accepts a `null` grant precisely so a page does not report a
 * scope gap it inferred (see `useSubscriptions.ts`) — the cost of being wrong there is one refused
 * request. For a mutation it is not: `Invalid scope` and `Token is expired` share a status, a `POST`
 * that fails may still have happened, and "did my playlist get created?" is a question a retry cannot
 * answer. So a caller that knows its grant passes it, and a call the grant provably cannot make never
 * reaches the wire.
 *
 * **JSON, always.** Measured (`playlist/manage.ts`): the same create sent as
 * `application/x-www-form-urlencoded` answers `400 {"error":"Invalid title."}` — the fields are simply
 * not read. There is no tolerant fallback to offer.
 *
 * **A `204` is a success with no body**, which is what four of the five playlist mutations answer, so
 * `null` is a real result here rather than an absence to guard against. Anything else that parses is
 * handed back raw for the caller's parser — `POST` answers `201` with a body worth reading in both
 * cases (a `playlistId`, or the whole entry row).
 */
export async function authWrite(
  platform: Platform,
  method: Exclude<HttpMethod, 'GET' | 'HEAD'>,
  path: string,
  /**
   * `unknown` values since 3e, where the playlist writes only ever needed strings. The preferences
   * body is 35 fields of mixed types — booleans, integers, floats and a string array — and *the types
   * are load-bearing*: one wrong-typed field discards the entire write with a `204` (API reference
   * §8d). Widening the parameter is what lets `preferencesWriteBody` be the thing that gets them
   * right, rather than a caller stringifying numbers here and hoping.
   */
  body: Readonly<Record<string, unknown>> | null,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<unknown, AuthApiProblem>> {
  const url = resolveInstanceUrl(platform.config, path);
  if (url === null) return err({ kind: 'bad-url', path });

  const endpoint = authEndpointOf(path);
  if (endpoint !== null && grantedScopes !== null && !scopesAllow(grantedScopes, method, endpoint)) {
    return err({ kind: 'out-of-scope', method, endpoint });
  }

  const response = await platform.transport.send({
    method,
    url,
    headers: body === null ? {} : { 'Content-Type': 'application/json' },
    body: body === null ? undefined : JSON.stringify(body),
    signal,
  });
  if (!response.ok) return err({ kind: 'transport', error: response.error });

  // `204 No Content` — and a `200` with an empty body would mean the same thing to every caller here.
  // Note this is the *opposite* reading from `authGetUrl`'s, and correctly so: a write has nothing to say,
  // while a read that says nothing has failed.
  if (response.value.body.trim() === '') return ok(null);

  try {
    return ok(JSON.parse(response.value.body) as unknown);
  } catch {
    return err({ kind: 'malformed-json' });
  }
}

/**
 * `POST /api/v1/auth/tokens/register` — mint this session's successor (API reference §8e).
 *
 * Returns the **raw response body**, not parsed JSON, and that is the point: the body *is* the new
 * credential, byte for byte what `Authorization: Bearer` must carry. Parsing and re-serialising it
 * here would hand storage a string the instance never wrote — and the caller has to validate the
 * claims anyway (`renewedClaimsUsable`), because the endpoint's two measured failure modes both
 * arrive as a well-formed `200`: a verbatim-stored duration `expire` (a token dead since 1970) and
 * silently stripped scopes (a successor that can do nothing).
 */
export async function registerRenewedToken(
  platform: Platform,
  body: Readonly<Record<string, unknown>>,
): Promise<Result<string, AuthApiProblem>> {
  const url = resolveInstanceUrl(platform.config, TOKEN_REGISTER_PATH);
  if (url === null) return err({ kind: 'bad-url', path: TOKEN_REGISTER_PATH });

  const response = await platform.transport.send({
    method: 'POST',
    url,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) return err({ kind: 'transport', error: response.error });
  if (response.value.body.trim() === '') return err({ kind: 'malformed-json' });
  return ok(response.value.body);
}

/**
 * `POST /api/v1/auth/tokens/unregister` — end the predecessor after a renewal. **Self-only**, and
 * that is measured rather than assumed (§8e): revoking any other session answers `400`, so this
 * call carries the *predecessor's own* bearer explicitly — by the time it is made, the store
 * already holds the successor, and a caller-supplied `Authorization` wins over the transport's.
 * Best-effort by design: a predecessor that could not be revoked merely ages out.
 */
export async function unregisterOwnSession(
  platform: Platform,
  bearerToken: string,
  session: string,
): Promise<void> {
  const url = resolveInstanceUrl(platform.config, TOKEN_UNREGISTER_PATH);
  if (url === null) return;
  await platform.transport.send({
    method: 'POST',
    url,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearerToken}` },
    body: JSON.stringify({ session }),
  });
}

export async function fetchSubscriptions(
  platform: Platform,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<ParsedList<SubscriptionRef>, AuthApiProblem>> {
  const raw = await authGet(platform, SUBSCRIPTIONS_PATH, grantedScopes, signal);
  if (!raw.ok) return err(raw.error);
  return ok(parseSubscriptionList(raw.value));
}

/**
 * Subscribe / unsubscribe — `POST`/`DELETE` on the same path, both `204`, both idempotent (measured:
 * a second subscribe does not duplicate, unsubscribing a stranger is `204` too). The id is validated
 * *before* the path is built (`subscriptionChangePath`): the measured empty-id answer is a routing
 * miss dressed as "signed out", and shipping that sentence about our own URL bug is the history
 * lesson again. A channel that does not exist is refused
 * `403 {"error":"Deleted or invalid channel"}`, which classifies as `client` — this endpoint
 * validates existence, so the refusal is reachable through any deleted channel.
 */
export async function changeSubscription(
  platform: Platform,
  action: 'subscribe' | 'unsubscribe',
  channelId: string,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<null, AuthApiProblem>> {
  const path = subscriptionChangePath(channelId);
  if (path === null) return err({ kind: 'bad-url', path: SUBSCRIPTIONS_PATH });

  const result = await authWrite(
    platform,
    action === 'subscribe' ? 'POST' : 'DELETE',
    path,
    null,
    grantedScopes,
    signal,
  );
  return result.ok ? ok(null) : err(result.error);
}

/**
 * One page of §8's subscription feed.
 *
 * The parameters go through `subscriptionFeedWireParams` rather than being interpolated here, and that
 * is not tidiness: it is where the two clamps live. `page=0` answers `403` with a leaked Postgres error
 * and `max_results=0` answers `200` with two empty arrays — one failure that lies about *what kind* of
 * failure it is, and one that does not look like a failure at all (`subscriptions/feed.ts`).
 */
export async function fetchSubscriptionFeed(
  platform: Platform,
  page: number,
  maxResults: number,
  grantedScopes: readonly AuthScope[] | null,
  signal?: AbortLike,
): Promise<Result<SubscriptionFeedPage, AuthApiProblem>> {
  const path = SUBSCRIPTION_FEED_PATH;
  const base = resolveInstanceUrl(platform.config, path);
  if (base === null) return err({ kind: 'bad-url', path });

  const url = withQuery(base, subscriptionFeedWireParams(page, maxResults));
  if (url === null) return err({ kind: 'bad-url', path });

  const raw = await authGetUrl(platform, path, url, grantedScopes, signal);
  if (!raw.ok) return err(raw.error);
  return ok(parseSubscriptionFeedPage(raw.value, platform.parseContext));
}
